import React from 'react';
import { MarkdownRenderer } from "../MarkdownRenderer";
import { DefaultToolCallRenderer } from '../ToolRenderers';
import { Button } from "../../ui/button";
import { ExternalLink, GitBranch, GitCommit, GitPullRequest } from 'lucide-react';

// Message interface for the CreateIssueToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// GitHub Issue response structure
interface GitHubIssue {
    html_url: string;
    number: number;
    title: string;
    state: string;
    body: string;
    created_at: string;
    user: {
        login: string;
        avatar_url: string;
        html_url: string;
    };
    labels: Array<{
        name: string;
        color: string;
    }>;
    assignees: Array<{
        login: string;
        avatar_url: string;
        html_url: string;
    }>;
}

// Tool renderer for GitHub issues
export function CreateIssueToolRenderer({ message, isToolCall }: ToolRendererProps) {
    // If this is just the tool call and not the response, render the default tool call renderer
    if (!message.tool_call_response_content) {
        return <DefaultToolCallRenderer message={message} />;
    }

    let issue: GitHubIssue | null = null;
    try {
        issue = JSON.parse(message.tool_call_response_content);
    } catch (error) {
        console.error('Error parsing GitHub issue response data:', error);
        return null;
    }

    // Validate response
    if (!issue || !issue.html_url || !issue.title) {
        return null;
    }

    // Format the creation date
    const createdAt = new Date(issue.created_at).toLocaleString();

    return (
        <div className="w-full space-y-4">
            <MarkdownRenderer content={message.content} />
            
            <div className="border border rounded overflow-hidden bg-backgroundmedium p-4">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                            issue.state === 'open' 
                                ? 'bg-green-500/10 text-green-400' 
                                : 'bg-red-500/10 text-red-400'
                        }`}>
                            {issue.state}
                        </span>
                        <span className="text-sm text-textmedium">#{issue.number}</span>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-textmedium hover:text-textlight"
                        onClick={() => window.open(issue.html_url, '_blank')}
                    >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View on GitHub
                    </Button>
                </div>

                <h3 className="text-lg font-semibold text-textlight mt-2">{issue.title}</h3>

                <div className="flex items-center gap-4 mt-2 text-sm text-textmedium">
                    <div className="flex items-center gap-2">
                        <img 
                            src={issue.user.avatar_url} 
                            alt={issue.user.login}
                            className="w-5 h-5 rounded-full"
                        />
                        <a 
                            href={issue.user.html_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-textlight"
                        >
                            {issue.user.login}
                        </a>
                    </div>
                    <span>•</span>
                    <span>Created {createdAt}</span>
                </div>

                {issue.labels.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                        {issue.labels.map((label) => (
                            <span
                                key={label.name}
                                className="px-2 py-1 rounded text-xs font-medium"
                                style={{
                                    backgroundColor: `#${label.color}20`,
                                    color: `#${label.color}`
                                }}
                            >
                                {label.name}
                            </span>
                        ))}
                    </div>
                )}

                {issue.assignees.length > 0 && (
                    <div className="flex items-center gap-2 mt-3">
                        <span className="text-sm text-textmedium">Assigned to:</span>
                        {issue.assignees.map((assignee) => (
                            <a
                                key={assignee.login}
                                href={assignee.html_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 hover:text-textlight"
                            >
                                <img 
                                    src={assignee.avatar_url} 
                                    alt={assignee.login}
                                    className="w-5 h-5 rounded-full"
                                />
                                <span className="text-sm">{assignee.login}</span>
                            </a>
                        ))}
                    </div>
                )}

                <div className="mt-4 prose prose-invert max-w-none [&>ul]:list-disc [&>ul]:pl-6 [&>ul>li]:mt-1">
                    <MarkdownRenderer content={issue.body} />
                </div>
            </div>
        </div>
    );
}

// Static method to determine if this renderer should render a message
CreateIssueToolRenderer.shouldRender = (message: Message): boolean => {
    return message.tool_name === "create_issue";
}; 